import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Link } from "@remix-run/react";
import { Trophy, Users, Clock, Sparkles, ArrowRight } from "lucide-react";

import PublicLeagueCard from "~/components/cards/PublicLeagueCard";
import { getAuthenticatedUser } from "~/services/auth.server";
import { defaultMetaTagsBuilder } from "~/utils/meta";
import { getAllPublicLeagues } from "~/services/league/league.server";
import { Button } from "~/components/ui/button";

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Home",
    description: "View all the exciting leagues on 7Qz",
    url: url,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  const allPublicLeagues = await getAllPublicLeagues();
  const user = await getAuthenticatedUser(request);

  if (!allPublicLeagues.success || !allPublicLeagues.payload) {
    throw new Error("Could not get public leagues");
  }

  const activeLeagues = allPublicLeagues.payload.filter((league) => {
    return league.closing_date_time
      ? league.closing_date_time.getTime() >= new Date().getTime()
      : false;
  });

  const inactiveLeagues = allPublicLeagues.payload.filter((league) => {
    return league.closing_date_time
      ? league.closing_date_time.getTime() < new Date().getTime()
      : false;
  });

  return {
    payload: {
      activeLeagues,
      inactiveLeagues,
    },
    user,
  };
}

export default function Index() {
  const { payload, user } = useLoaderData<typeof loader>();

  const totalActiveLeagues = payload.activeLeagues.length;
  const totalPlayers = payload.activeLeagues.reduce(
    (sum, league) => sum + league._count.bets,
    0
  );

  return (
    <div className="flex flex-col">
      {/* Leagues Section */}
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {payload.activeLeagues.length > 0 && (
            <div className="mb-16">
              {/* Section Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Clock className="w-4 h-4" />
                  Live Now
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Active Leagues
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Join these exciting prediction leagues and compete for amazing
                  prizes
                </p>
              </div>

              {/* League Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {payload.activeLeagues.map((league) => (
                  <div key={league.id} className="group">
                    <PublicLeagueCard league={league} user={user} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {payload.inactiveLeagues.length > 0 && (
            <div>
              {/* Section Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-muted/50 text-muted-foreground px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Trophy className="w-4 h-4" />
                  Completed
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Recent Leagues
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Check out the results from recently completed leagues
                </p>
              </div>

              {/* League Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {payload.inactiveLeagues.map((league) => (
                  <div key={league.id} className="group">
                    <PublicLeagueCard league={league} user={user} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {payload.activeLeagues.length === 0 &&
            payload.inactiveLeagues.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-12 h-12 text-muted-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  No Leagues Available
                </h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  New leagues are added regularly. Check back soon for exciting
                  new prediction challenges!
                </p>
                {!user && (
                  <Link to="/signup">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white px-8 py-4 text-lg font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Join 7Qz
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
